import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

const REEL = Array.from({ length: 30 }, (_, index) => index % 10)

interface DigitReelProps {
    digit: number
    play: boolean
    delay: number
    duration: number
    reduced: boolean
}

// One odometer column. It rests on the middle copy of 0-9 (index 10-19) and always rolls upward,
// then snaps back to the equivalent resting index without a transition.
const DigitReel: FC<DigitReelProps> = ({ digit, play, delay, duration, reduced }): ReactNode => {
    const reelRef = useRef<HTMLSpanElement>(null)
    const [initialIndex] = useState(() => (play ? 10 + digit : 10))
    const position = useRef(initialIndex)
    const started = useRef(play)

    useEffect(() => {
        const reel = reelRef.current

        if (!reel) return

        const place = (index: number, animate: boolean, wait = 0) => {
            reel.style.transition = animate ? `transform ${duration}ms var(--fc-ease-expo) ${wait}ms` : 'none'
            reel.style.transform = `translate3d(0, -${index}em, 0)`
        }

        if (!play) {
            started.current = false
            position.current = 10
            place(10, false)
            return
        }

        if (reduced) {
            started.current = true
            position.current = 10 + digit
            place(10 + digit, false)
            return
        }

        let from = started.current ? position.current : 10

        if (from >= 20) {
            from -= 10
            place(from, false)
        }

        const steps = started.current ? (digit - (from % 10) + 10) % 10 : 10 + digit

        if (steps === 0) return

        if (!started.current) place(10, false)

        void reel.offsetHeight

        const target = from + steps

        started.current = true
        position.current = target
        place(target, true, delay)

        const timer = window.setTimeout(() => {
            position.current = 10 + digit
            place(10 + digit, false)
        }, delay + duration + 60)

        return () => window.clearTimeout(timer)
    }, [digit, play, delay, duration, reduced])

    return (
        <span className='fc-reel-window' style={{ width: '1ch' }}>
            <span
                ref={reelRef}
                className='fc-reel'
                style={{ transform: `translate3d(0, -${initialIndex}em, 0)` }}
            >
                {REEL.map((value, index) => (
                    <span key={index}>
                        {value}
                    </span>
                ))}
            </span>
        </span>
    )
}

interface SlidingNumberProps {
    value: string
    play?: boolean
    duration?: number
    stagger?: number
    className?: string
}

// Odometer-style number. Digits spin upward into place when `play` turns on and again whenever the value changes.
export const SlidingNumber: FC<SlidingNumberProps> = ({
    value,
    play = true,
    duration = 1400,
    stagger = 60,
    className
}): ReactNode => {
    const reduced = useReducedMotion()
    const characters = value.split('')

    let digitOrder = 0

    return (
        <span className={cn('inline-flex items-start leading-none tabular-nums', className)}>
            <span className='sr-only'>
                {value}
            </span>

            <span aria-hidden='true' className='inline-flex items-start leading-none'>
                {characters.map((character, index) => {
                    const keyFromEnd = characters.length - index

                    if (/\d/.test(character)) {
                        const order = digitOrder

                        digitOrder += 1

                        return (
                            <DigitReel
                                key={`digit-${keyFromEnd}`}
                                digit={Number(character)}
                                play={play}
                                delay={order * stagger}
                                duration={duration}
                                reduced={reduced}
                            />
                        )
                    }

                    return (
                        <span key={`symbol-${keyFromEnd}`} className='inline-block h-[1em] whitespace-pre leading-none'>
                            {character}
                        </span>
                    )
                })}
            </span>
        </span>
    )
}
